import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createIndexedDbRepository } from '../data/indexedDbRepository';
import type { PlacesRepository } from '../data/repository';
import type { Place, PlaceChanges, PlaceInput, UserData } from '../domain/types';

const EMPTY_DATA: UserData = { places: [], favoriteStationIds: [], customTags: [] };

interface AppContextValue {
  data: UserData;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  createPlace: (input: PlaceInput) => Promise<Place>;
  updatePlace: (id: string, changes: PlaceChanges) => Promise<Place>;
  deletePlace: (id: string) => Promise<void>;
  setStationFavorite: (stationId: string, favorite: boolean) => Promise<void>;
  replaceAll: (snapshot: UserData) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [repository] = useState<PlacesRepository>(() => createIndexedDbRepository());
  const [data, setData] = useState<UserData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const snapshot = await repository.load();
      setData(snapshot);
      setError(null);
    } catch (cause) {
      console.error(cause);
      setError('Не удалось открыть локальные данные. Проверьте настройки Safari и попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  }, [repository]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(async (input: PlaceInput) => {
    const place = await repository.createPlace(input);
    try {
      await repository.rememberTags(place.tags);
    } catch (cause) {
      // The tags still remain discoverable from the saved place. A settings
      // write must not make a successfully created place look unsaved.
      console.warn('Не удалось отдельно запомнить теги', cause);
    }
    await reload();
    return place;
  }, [reload, repository]);

  const update = useCallback(async (id: string, changes: PlaceChanges) => {
    const place = await repository.updatePlace(id, changes);
    try {
      await repository.rememberTags(place.tags);
    } catch (cause) {
      console.warn('Не удалось отдельно запомнить теги', cause);
    }
    await reload();
    return place;
  }, [reload, repository]);

  const remove = useCallback(async (id: string) => {
    await repository.deletePlace(id);
    await reload();
  }, [reload, repository]);

  const setFavorite = useCallback(async (stationId: string, favorite: boolean) => {
    await repository.setStationFavorite(stationId, favorite);
    await reload();
  }, [reload, repository]);

  const replaceAll = useCallback(async (snapshot: UserData) => {
    await repository.replaceAll(snapshot);
    await reload();
  }, [reload, repository]);

  const value = useMemo<AppContextValue>(() => ({
    data,
    loading,
    error,
    reload,
    createPlace: create,
    updatePlace: update,
    deletePlace: remove,
    setStationFavorite: setFavorite,
    replaceAll,
  }), [create, data, error, loading, reload, remove, replaceAll, setFavorite, update]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppData(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppData должен использоваться внутри AppProvider.');
  return context;
}
